import React, { useState } from 'react';
import { Music, Mic, MapPin, FileText, List, Calendar, Link, ChevronLeft, ChevronRight, X } from 'lucide-react';

export default function WelcomeScreen({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Detect language from localStorage (same pattern as AuthScreen)
  const detectLanguage = () => {
    const saved = localStorage.getItem('tapsody_language');
    if (saved) return saved;
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('nl') ? 'nl' : 'en';
  };

  const [language] = useState(detectLanguage());

  const content = {
    en: {
      steps: [
        {
          icon: null,
          title: 'Welcome to Tapsody!',
          description: 'Your all-in-one band dashboard. Share your invite code from Settings to bring your bandmates on board.',
          showLogo: true,
          gradient: 'from-brand to-brand-magenta',
        },
        {
          icon: Music,
          secondIcon: Mic,
          title: 'Manage Your Music',
          description: 'Add songs with multiple versions, lyrics, and tempo. Leave comments for your bandmates, and capture ideas on the go with voice memos.',
          gradient: 'from-brand-magenta to-brand-aqua',
        },
        {
          icon: List,
          title: 'Build Setlists',
          description: 'Create setlists with drag-and-drop ordering and gap times. Play through your set sequentially to rehearse like a real gig.',
          gradient: 'from-brand-aqua to-brand',
        },
        {
          icon: MapPin,
          secondIcon: Calendar,
          title: 'Find Gigs & Venues',
          description: 'Browse 1,750+ Dutch venues and festivals filtered by genre and region. Track your upcoming and past gigs in one place.',
          gradient: 'from-brand to-brand-aqua',
        },
        {
          icon: FileText,
          secondIcon: Link,
          title: 'Build & Share Your EPK',
          description: 'Create a professional electronic press kit with photos, bios, tech riders, and demo songs. Share it with venues and bookers in one tap.',
          gradient: 'from-brand-aqua to-brand-magenta',
        },
      ],
      letsGo: "Let's Go!",
      skip: 'Skip Tutorial',
    },
    nl: {
      steps: [
        {
          icon: null,
          title: 'Welkom bij Tapsody!',
          description: 'Jouw alles-in-één band dashboard. Deel je uitnodigingscode via Instellingen om je bandleden uit te nodigen.',
          showLogo: true,
          gradient: 'from-brand to-brand-magenta',
        },
        {
          icon: Music,
          secondIcon: Mic,
          title: 'Beheer Je Muziek',
          description: 'Voeg nummers toe met meerdere versies, teksten en tempo. Geef feedback aan je bandleden en leg ideeën vast met spraakmemo\'s.',
          gradient: 'from-brand-magenta to-brand-aqua',
        },
        {
          icon: List,
          title: 'Maak Setlists',
          description: 'Stel setlists samen met drag-and-drop en pauzetijden. Speel je set achtereenvolgens af om te oefenen als een echte show.',
          gradient: 'from-brand-aqua to-brand',
        },
        {
          icon: MapPin,
          secondIcon: Calendar,
          title: 'Vind Shows & Podia',
          description: 'Blader door 1.750+ Nederlandse podia en festivals, gefilterd op genre en regio. Houd je aankomende en eerdere shows bij op één plek.',
          gradient: 'from-brand to-brand-aqua',
        },
        {
          icon: FileText,
          secondIcon: Link,
          title: 'Bouw & Deel Je EPK',
          description: 'Maak een professionele elektronische perskit met foto\'s, bio\'s, tech riders en demo\'s. Deel het met podia en bookers in één tik.',
          gradient: 'from-brand-aqua to-brand-magenta',
        },
      ],
      letsGo: 'Aan de slag!',
      skip: 'Tutorial overslaan',
    },
  };

  const t = content[language] || content.en;
  const steps = t.steps;

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    if (distance > 50 && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
    if (distance < -50 && currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const goToNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const goToPrevious = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const step = steps[currentStep];
  const CurrentIcon = step.icon;
  const SecondIcon = step.secondIcon || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

      {/* Floating card */}
      <div
        className="relative w-full max-w-md card-3d rounded-card p-6 sm:p-8 text-white"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-transparent hover:bg-brand transition p-2 rounded-full"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="text-center">
          {/* Icon or Logo */}
          {step.showLogo ? (
            <div className="mb-6 flex justify-center">
              <img
                src={`${process.env.PUBLIC_URL}/tapsody-logo.png`}
                alt="Tapsody Logo"
                className="h-20 w-auto"
              />
            </div>
          ) : (
            <div className="mb-6 flex justify-center gap-3">
              <div className={`w-16 h-16 bg-gradient-to-br ${step.gradient} rounded-full flex items-center justify-center`}>
                <CurrentIcon className="w-8 h-8 text-white" />
              </div>
              {SecondIcon && (
                <div className={`w-16 h-16 bg-gradient-to-br ${step.gradient} rounded-full flex items-center justify-center opacity-70`}>
                  <SecondIcon className="w-8 h-8 text-white" />
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <h1 className="text-2xl font-bold mb-3 text-white tracking-title">
            {step.title}
          </h1>

          {/* Description */}
          <p className="text-base font-medium text-text-muted mb-8 px-2 leading-relaxed">
            {step.description}
          </p>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-brand w-8'
                    : 'bg-white/20 w-2'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4">
            {/* Previous */}
            <button
              onClick={goToPrevious}
              disabled={currentStep === 0}
              className={`p-3 rounded-full transition border ${
                currentStep === 0
                  ? 'border-surface-border-light text-text-subtle opacity-disabled cursor-not-allowed'
                  : 'border-surface-border-light hover:bg-white/5 text-white'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Center: Let's Go on last step */}
            {currentStep === steps.length - 1 ? (
              <button
                onClick={onClose}
                className="bg-gradient-to-r from-brand to-brand-magenta hover:opacity-90 transition px-8 h-btn font-bold text-lg text-white rounded-pill"
              >
                {t.letsGo}
              </button>
            ) : (
              <button
                onClick={goToNext}
                className="bg-brand hover:bg-brand-hover transition px-8 h-btn font-semibold text-white rounded-pill"
              >
                {language === 'nl' ? 'Volgende' : 'Next'}
              </button>
            )}

            {/* Next */}
            <button
              onClick={goToNext}
              disabled={currentStep === steps.length - 1}
              className={`p-3 rounded-full transition border ${
                currentStep === steps.length - 1
                  ? 'border-surface-border-light text-text-subtle opacity-disabled cursor-not-allowed'
                  : 'border-surface-border-light hover:bg-white/5 text-white'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Skip */}
          {currentStep < steps.length - 1 && (
            <button
              onClick={onClose}
              className="mt-5 text-text-subtle hover:text-white transition text-sm font-medium"
            >
              {t.skip}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
